import React, { useState, useEffect } from "react";
import { C, getPerms } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, ReportsTab, OperationalPlanTab, useToast
} from "./erp-shared";
import {
  getMeetings, createMeeting,
  getBoardMembers, createBoardMember,
  getBoardDecisions, createBoardDecision,
  getBoardCommittees, createBoardCommittee,
} from "./supabaseClient";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  Scale, Users, FileText, Gavel,
  CheckCircle, Clock, Plus, Search,
  Calendar, Star,
  Phone, Paperclip,
  Target, Layers, Video, FileSignature, Check
} from "lucide-react";

/* ✦ محوّلات صفوف DB (snake_case) → camelCase الذي تقرأه الواجهة. */
function mapMemberRow(r) {
  return {
    id: r.id,
    name: r.name,
    position: r.position || "عضو",
    phone: r.phone || "",
    email: r.email || "",
    nationalId: r.national_id || "",
    birthDate: r.birth_date || "",
    joinDate: r.join_date || "",
    qualification: r.qualification || "",
    experience: r.experience || "",
    status: r.status || "active",
    committees: r.committees || [],
    notes: r.notes || "",
  };
}

function mapDecisionRow(r) {
  return {
    id: r.id,
    number: r.number || "",
    meeting: r.meeting || "—",
    date: r.decision_date || "",
    title: r.title || "",
    details: r.details || "",
    responsible: r.responsible || "",
    deadline: r.deadline || "",
    status: r.status || "pending",
    notes: r.notes || null,
    signatures: r.signatures || {},
    signatureDates: r.signature_dates || {},
    execApproved: r.exec_approved === true,
    execApprovalDate: r.exec_approval_date || "",
  };
}

function mapCommitteeRow(r) {
  return {
    id: r.id,
    name: r.name,
    description: r.description || "",
    chair: r.chair || "",
    status: r.status || "active",
    color: r.color || "#7c3aed",
    members: r.members || [],
    goals: r.goals || "",
    authorities: r.authorities || "",
    foundingDate: r.founding_date || "",
    meetingFrequency: r.meeting_frequency || "",
    decisionsCount: Number(r.decisions_count) || 0,
    meetingsCount: Number(r.meetings_count) || 0,
  };
}

/* ═════════════════════════════════════════════
   ✦ Board Page — المجلس والقرارات
   ═════════════════════════════════════════════ */
export default function BoardPage({ user }) {
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [showQuickDecision, setShowQuickDecision] = useState(false);
  const [showQuickCommittee, setShowQuickCommittee] = useState(false);
  const [showQuickMeeting, setShowQuickMeeting] = useState(false);
  const [showToast, ToastView] = useToast();
  const perms = getPerms(user);

  // جلب اجتماعات المجلس الحقيقية (module="board") — مصدر واحد يتشاركه تبويب الاجتماعات
  // ونماذج القرار (حقل "الاجتماع المرتبط"). الحقول الغنية عرض-فقط ولا تصمد بعد إعادة التحميل.
  useEffect(() => {
    let alive = true;
    getMeetings("board")
      .then((rows) => { if (alive) setMeetings((rows || []).map(mapBoardRow)); })
      .catch((e) => { if (alive) showToast(e?.message || "تعذّر جلب الاجتماعات", "error"); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // جلب الأعضاء والقرارات واللجان الحيّة من Supabase.
  useEffect(() => {
    getBoardMembers().then((d) => setMembers((d || []).map(mapMemberRow))).catch(() => {});
    getBoardDecisions().then((d) => setDecisions((d || []).map(mapDecisionRow))).catch(() => {});
    getBoardCommittees().then((d) => setCommittees((d || []).map(mapCommitteeRow))).catch(() => {});
  }, []);

  const tabs = [
    { key: "members", label: "أعضاء المجلس", icon: Users },
    { key: "decisions", label: "القرارات", icon: Gavel },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "committees", label: "اللجان", icon: Layers },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  const btnStyle = {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: "#fff", border: "1px solid #e5e7eb", cursor: "pointer",
    fontFamily: "'Readex Pro'", fontSize: 13, fontWeight: 600, color: "#003d58",
  };

  const DECISION_STATUS_LABELS = { approved: "معتمد", pending: "قيد الدراسة", implemented: "تم التنفيذ", rejected: "مرفوض", overdue: "متأخر" };
  const reportSources = [
    {
      label: "القرارات", mode: "monthly", dateField: "date", rows: decisions,
      columns: [
        { key: "number", label: "الرقم" },
        { key: "date", label: "التاريخ" },
        { key: "title", label: "عنوان القرار" },
        { key: "responsible", label: "المسؤول" },
        { key: "status", label: "الحالة", format: (v) => DECISION_STATUS_LABELS[v] || v },
      ],
    },
    { label: "أعضاء المجلس", mode: "cumulative", rows: members },
    { label: "اللجان", mode: "cumulative", rows: committees },
  ];

  return (
    <div>
      {ToastView}
      <ModuleHeader icon={Scale} title="مجلس الإدارة" subtitle="إدارة أعضاء مجلس الإدارة والقرارات واللجان والاجتماعات">
        <MonthlyReportButton moduleTitle="مجلس الإدارة" sources={reportSources} />
      </ModuleHeader>

      {perms.canEdit && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <button onClick={() => setShowQuickDecision(true)} style={btnStyle}>
            <Gavel size={16} color="#7c3aed" />
            إضافة قرار
          </button>
          <button onClick={() => setShowQuickCommittee(true)} style={btnStyle}>
            <Layers size={16} color="#6366f1" />
            إنشاء لجنة
          </button>
          <button onClick={() => setShowQuickMeeting(true)} style={btnStyle}>
            <Calendar size={16} color={C.teal} />
            جدولة اجتماع
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "members" && <MembersTab canEdit={perms.canEdit} members={members} setMembers={setMembers} />}
      {tab === "decisions" && <DecisionsTab canEdit={perms.canEdit} decisions={decisions} setDecisions={setDecisions} meetings={meetings} members={members} />}
      {tab === "meetings" && <BoardMeetingsTab canEdit={perms.canEdit} meetings={meetings} setMeetings={setMeetings} />}
      {tab === "committees" && <CommitteesTab canEdit={perms.canEdit} committees={committees} setCommittees={setCommittees} members={members} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="board" />}
      {tab === "reports" && <ReportsTab departmentName="المجلس" />}

      {showQuickDecision && (
        <QuickDecisionForm
          onClose={() => setShowQuickDecision(false)}
          onSave={(d) => { setDecisions(prev => [d, ...prev]); showToast("تمت إضافة القرار"); }}
          decisionsCount={decisions.length}
          meetings={meetings}
          members={members}
        />
      )}
      {showQuickCommittee && (
        <QuickCommitteeForm
          onClose={() => setShowQuickCommittee(false)}
          onSave={(c) => { setCommittees(prev => [...prev, c]); showToast("تم إنشاء اللجنة"); }}
          members={members}
        />
      )}
      {showQuickMeeting && (
        <QuickMeetingForm
          onClose={() => setShowQuickMeeting(false)}
          onSave={(m) => { if (m) setMeetings(prev => [m, ...prev]); showToast("تم حفظ الاجتماع"); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Members Tab — أعضاء المجلس
   ───────────────────────────────────────────── */
const BOARD_POSITIONS = ["رئيس المجلس", "نائب الرئيس", "عضو", "أمين السر", "أمين الصندوق"];
const BOARD_COMMITTEES_OPTIONS = ["اللجنة التنفيذية", "لجنة المالية والاستثمار", "لجنة التطوير والجودة", "لجنة خدمة المجتمع", "لجنة التدقيق"];
const INITIAL_MEMBER = {
  name: "", position: "عضو",
  phone: "", email: "", nationalId: "", birthDate: "",
  joinDate: "", qualification: "", experience: "",
  committees: [], notes: "",
};

function MembersTab({ canEdit, members, setMembers }) {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_MEMBER);
  const [showToast, ToastView] = useToast();

  const filtered = members.filter(m =>
    m.name.includes(search) || m.position.includes(search)
  );

  function toggleMemberCommittee(c) {
    setForm(prev => ({
      ...prev,
      committees: prev.committees.includes(c) ? prev.committees.filter(x => x !== c) : [...prev.committees, c],
    }));
  }

  async function handleSave() {
    if (!form.name) {
      showToast("الاسم مطلوب", "error");
      return;
    }
    const payload = {
      name: form.name,
      position: form.position || "عضو",
      phone: form.phone,
      email: form.email,
      national_id: form.nationalId,
      birth_date: form.birthDate,
      join_date: form.joinDate || "",
      qualification: form.qualification,
      experience: form.experience,
      status: "active",
      committees: [...form.committees],
      notes: form.notes,
    };
    try {
      const created = await createBoardMember(payload);
      setMembers(prev => [...prev, mapMemberRow(created)]);
      setForm(INITIAL_MEMBER);
      setShowAdd(false);
      showToast("تمت إضافة العضو");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ العضو", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      {/* KPIs */}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الأعضاء" value={members.length} icon={Users} color="#7c3aed" />
        <KPICard title="أعضاء فاعلين" value={members.filter(m => m.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="رئيس المجلس" value={members.filter(m => m.role === "رئيس المجلس").length} icon={Star} color={C.accent} />
        <KPICard title="اللجان الفرعية" value="0" icon={Layers} color={C.teal} />
      </div>

      {/* Toolbar */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input
              type="text" placeholder="بحث بالاسم أو المنصب..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          {canEdit && (
            <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">
              إضافة عضو
            </Btn>
          )}
        </div>
      </Card>

      {/* Members Grid */}
      <div style={styles.membersGrid}>
        {filtered.map(m => (
          <Card key={m.id} style={styles.memberCard} onClick={() => setSelected(m)}>
            <div style={styles.memberTop}>
              <div style={{
                ...styles.memberAvatar,
                background: m.position === "رئيس المجلس"
                  ? `linear-gradient(135deg, ${C.accent}, #b8941a)`
                  : `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
              }}>
                {m.name.charAt(0)}
              </div>
              <div style={{
                ...styles.memberStatusDot,
                background: m.status === "active" ? C.success : C.textLight,
              }} />
            </div>
            <h4 style={styles.memberName}>{m.name}</h4>
            <p style={styles.memberPosition}>{m.position}</p>
            <div style={styles.memberMeta}>
              <span style={styles.memberMetaItem}>
                <Calendar size={12} /> عضو منذ {m.joinDate}
              </span>
              <span style={styles.memberMetaItem}>
                <Phone size={12} /> {m.phone}
              </span>
            </div>
            {m.committees && (
              <div style={styles.memberTags}>
                {m.committees.map((c, i) => (
                  <span key={i} style={styles.tag}>{c}</span>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Detail Modal */}
      {selected && (
        <Modal title={`عضو المجلس — ${selected.name}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>المنصب:</strong> {selected.position}</div>
            <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
            <div style={styles.detailRow}><strong>تاريخ الانضمام:</strong> {selected.joinDate}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> {selected.status === "active" ? "فاعل" : "غير فاعل"}</div>
            {selected.committees && (
              <div style={styles.detailRow}>
                <strong>اللجان:</strong>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                  {selected.committees.map((c, i) => <span key={i} style={styles.tag}>{c}</span>)}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="إضافة عضو مجلس" onClose={() => { setShowAdd(false); setForm(INITIAL_MEMBER); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="الاسم الكامل" required placeholder="الاسم الثلاثي للعضو" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="المنصب" type="select" options={BOARD_POSITIONS} value={form.position} onChange={v => setForm({ ...form, position: v })} />
              <Field label="رقم الهوية" placeholder="10xxxxxxxx" value={form.nationalId} onChange={v => setForm({ ...form, nationalId: v })} />
              <Field label="تاريخ الميلاد" type="date" value={form.birthDate} onChange={v => setForm({ ...form, birthDate: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الهاتف" placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد الإلكتروني" placeholder="member@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="تاريخ الانضمام" type="date" value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
              <Field label="المؤهل العلمي" placeholder="مثال: دكتوراه إدارة" value={form.qualification} onChange={v => setForm({ ...form, qualification: v })} />
            </div>
            <Field label="الخبرات السابقة" type="textarea" rows={2} placeholder="الخبرات المهنية والقيادية..." value={form.experience} onChange={v => setForm({ ...form, experience: v })} />

            {/* Committees */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                اللجان ({form.committees.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                {BOARD_COMMITTEES_OPTIONS.map(c => (
                  <label key={c} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.committees.includes(c)} onChange={() => toggleMemberCommittee(c)} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-start", marginTop: 8 }}>
              <Btn icon={CheckCircle} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MEMBER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Decisions Tab — القرارات
   ───────────────────────────────────────────── */
const INITIAL_DECISION = {
  number: "", meeting: "", title: "", details: "", responsible: "",
  deadline: "", statusLabel: "قيد الدراسة", notes: "",
  signatures: {}, signatureDates: {},
  execApproved: false, execApprovalDate: "",
};

export function DecisionsTab({ canEdit, decisions: decisionsProp, setDecisions: setDecisionsProp, meetings = [], members: membersProp }) {
  // يُستخدم في المجلس (بروابط من الأب) وفي الحوكمة (مستقلاً بلا props) — لذا نجلب محليًا عند غياب الـ prop.
  const [decisionsLocal, setDecisionsLocal] = useState([]);
  const decisions = decisionsProp !== undefined ? decisionsProp : decisionsLocal;
  const setDecisions = setDecisionsProp || setDecisionsLocal;
  const [membersLocal, setMembersLocal] = useState([]);
  const members = membersProp !== undefined ? membersProp : membersLocal;
  useEffect(() => {
    if (decisionsProp === undefined) getBoardDecisions().then(d => setDecisionsLocal((d || []).map(mapDecisionRow))).catch(() => {});
    if (membersProp === undefined) getBoardMembers().then(d => setMembersLocal((d || []).map(mapMemberRow))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_DECISION);
  const [showToast, ToastView] = useToast();

  const filtered = decisions.filter(d => {
    const matchSearch = d.title.includes(search) || d.number.includes(search);
    const matchStatus = filterStatus === "all" || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const DECISION_STATUSES = [
    { key: "approved", label: "معتمد", color: C.success },
    { key: "pending", label: "قيد الدراسة", color: C.warning },
    { key: "implemented", label: "تم التنفيذ", color: C.teal },
    { key: "rejected", label: "مرفوض", color: C.danger },
    { key: "overdue", label: "متأخر", color: "#ea580c" },
  ];

  const labelToKey = { "معتمد": "approved", "قيد الدراسة": "pending", "تم التنفيذ": "implemented", "مرفوض": "rejected", "متأخر": "overdue" };

  function toggleSignature(name) {
    setForm(prev => {
      const newSigs = { ...prev.signatures };
      const newDates = { ...prev.signatureDates };
      if (newSigs[name]) {
        delete newSigs[name];
        delete newDates[name];
      } else {
        newSigs[name] = true;
        newDates[name] = new Date().toISOString().slice(0, 10);
      }
      return { ...prev, signatures: newSigs, signatureDates: newDates };
    });
  }

  function autoNumber() {
    const seq = decisions.length + 1;
    const yr = new Date().getFullYear();
    return `ق-${yr}-${String(seq).padStart(3, "0")}`;
  }

  function openAdd() {
    setForm({ ...INITIAL_DECISION, number: autoNumber() });
    setShowAdd(true);
  }

  async function handleSave() {
    if (!form.title || !form.details || !form.responsible) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      number: form.number || autoNumber(),
      meeting: form.meeting || "—",
      decision_date: today,
      title: form.title,
      details: form.details,
      responsible: form.responsible,
      deadline: form.deadline || "",
      status: labelToKey[form.statusLabel] || "pending",
      notes: form.notes || null,
      signatures: { ...form.signatures },
      signature_dates: { ...form.signatureDates },
      exec_approved: form.execApproved,
      exec_approval_date: form.execApprovalDate || (form.execApproved ? today : ""),
    };
    try {
      const created = await createBoardDecision(payload);
      setDecisions(prev => [mapDecisionRow(created), ...prev]);
      setForm(INITIAL_DECISION);
      setShowAdd(false);
      showToast("تمت إضافة القرار");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ القرار", "error");
    }
  }

  const signedCount = Object.keys(form.signatures || {}).length;

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      {/* KPIs */}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي القرارات" value={decisions.length} icon={Gavel} color="#7c3aed" />
        <KPICard title="معتمدة" value={decisions.filter(d => d.status === "approved").length} icon={CheckCircle} color={C.success} />
        <KPICard title="قيد الدراسة" value={decisions.filter(d => d.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="تم التنفيذ" value={decisions.filter(d => d.status === "implemented").length} icon={Target} color={C.teal} />
      </div>

      {/* Toolbar */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input
              type="text" placeholder="بحث بالعنوان أو الرقم..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">جميع الحالات</option>
              {DECISION_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={openAdd} size="sm">قرار جديد</Btn>}
          </div>
        </div>
      </Card>

      {/* Decisions Table */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "number", label: "رقم القرار", width: "100px", render: (v) => <span style={{ fontWeight: 600, color: C.primary }}>{v}</span> },
            { key: "title", label: "العنوان" },
            { key: "meeting", label: "الاجتماع", width: "120px" },
            { key: "date", label: "التاريخ", width: "110px" },
            { key: "responsible", label: "المسؤول", width: "130px" },
            { key: "status", label: "الحالة", width: "110px", render: (v) => <StatusBadge status={v} statuses={DECISION_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {/* Decision Detail */}
      {selected && (() => {
        const detailSigned = Object.keys(selected.signatures || {}).length;
        return (
        <Modal title={`القرار ${selected.number}`} onClose={() => setSelected(null)} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>رقم القرار:</strong> {selected.number}</div>
            <div style={styles.detailRow}><strong>العنوان:</strong> {selected.title}</div>
            <div style={styles.detailRow}><strong>الاجتماع:</strong> {selected.meeting}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>المسؤول:</strong> {selected.responsible}</div>
            {selected.deadline && <div style={styles.detailRow}><strong>الموعد النهائي:</strong> {selected.deadline}</div>}
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={DECISION_STATUSES} /></div>
            <div style={styles.detailRow}>
              <strong>التفاصيل:</strong>
              <p style={{ margin: "6px 0 0", fontSize: 14, color: C.primary, lineHeight: 1.8 }}>
                {selected.details}
              </p>
            </div>

            {/* Signatures */}
            <div style={{ padding: 14, background: `${C.teal}05`, borderRadius: 12, border: `1px solid ${C.teal}20` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <strong style={{ color: C.primary, display: "flex", alignItems: "center", gap: 6 }}>
                  <FileSignature size={16} color={C.teal} /> التوقيعات والموافقات
                </strong>
                <span style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: `${C.teal}15`, color: C.teal }}>
                  الموافقات: {detailSigned}/{members.length} أعضاء {selected.execApproved ? "+ المدير التنفيذي (معتمد)" : "+ المدير التنفيذي (بانتظار)"}
                </span>
              </div>

              {/* Approvers */}
              {detailSigned > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: C.success, display: "flex", alignItems: "center", gap: 4 }}>
                    <Check size={12} /> الموافقون ({detailSigned}):
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {members.filter(m => selected.signatures && selected.signatures[m.name]).map(m => (
                      <div key={m.id} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "6px 10px", background: C.white, borderRadius: 6, fontSize: 12,
                        border: `1px solid ${C.success}`,
                      }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <CheckCircle size={14} color={C.success} />
                          <span>{m.name}</span>
                        </span>
                        <span style={{ fontSize: 10, color: C.textLight }}>{selected.signatureDates?.[m.name]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Non-approvers */}
              {members.length - detailSigned > 0 && (
                <div>
                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: C.textLight }}>
                    بانتظار التوقيع ({members.length - detailSigned}):
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {members.filter(m => !(selected.signatures && selected.signatures[m.name])).map(m => (
                      <div key={m.id} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "6px 10px", background: C.white, borderRadius: 6, fontSize: 12,
                        border: `1px dashed ${C.border}`,
                        color: C.textLight,
                      }}>
                        <Clock size={14} />
                        <span>{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12, padding: 10, background: selected.execApproved ? `${C.success}10` : `${C.warning}10`, borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}>
                {selected.execApproved
                  ? <CheckCircle size={16} color={C.success} />
                  : <Clock size={16} color={C.warning} />}
                <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>
                  موافقة المدير التنفيذي: {selected.execApproved ? `معتمد (${selected.execApprovalDate || ""})` : "بانتظار"}
                </span>
              </div>
            </div>

            {selected.notes && (
              <div style={styles.detailRow}>
                <strong>ملاحظات:</strong>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textLight }}>{selected.notes}</p>
              </div>
            )}
          </div>
        </Modal>
        );
      })()}

      {/* Add Decision Modal */}
      {showAdd && (
        <Modal title="إضافة قرار جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_DECISION); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="رقم القرار (يولّد تلقائياً)" placeholder="ق-2026-001" value={form.number} onChange={v => setForm({ ...form, number: v })} />
              <Field label="الاجتماع المرتبط" type="select" options={meetings.map(m => m.title)} value={form.meeting} onChange={v => setForm({ ...form, meeting: v })} />
            </div>
            <Field label="عنوان القرار" required placeholder="أدخل عنوان القرار" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <Field label="تفاصيل القرار" type="textarea" required placeholder="نص القرار بالتفصيل..." rows={5} value={form.details} onChange={v => setForm({ ...form, details: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="المسؤول عن التنفيذ" required placeholder="اسم المسؤول" value={form.responsible} onChange={v => setForm({ ...form, responsible: v })} />
              <Field label="الموعد النهائي" type="date" value={form.deadline} onChange={v => setForm({ ...form, deadline: v })} />
              <Field label="الحالة" type="select" options={["قيد الدراسة","معتمد","تم التنفيذ","مرفوض","متأخر"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>

            {/* E-Signatures block */}
            <div style={{ padding: 12, background: `${C.teal}05`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong style={{ color: C.primary, display: "flex", alignItems: "center", gap: 6 }}>
                  <FileSignature size={16} color={C.teal} /> التوقيع الإلكتروني لأعضاء المجلس
                </strong>
                <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: `${C.teal}15`, color: C.teal }}>
                  الموافقات: {signedCount}/{members.length} أعضاء {form.execApproved ? "+ المدير التنفيذي (معتمد)" : "+ المدير التنفيذي (بانتظار)"}
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {members.map(m => {
                  const ok = !!form.signatures[m.name];
                  return (
                    <label key={m.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "6px 10px", background: C.white, borderRadius: 6, fontSize: 12,
                      border: `1px solid ${ok ? C.success : C.border}`, cursor: "pointer",
                    }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="checkbox" checked={ok} onChange={() => toggleSignature(m.name)} />
                        <span>{m.name}</span>
                      </span>
                      {ok && <span style={{ fontSize: 10, color: C.textLight }}>{form.signatureDates[m.name]}</span>}
                    </label>
                  );
                })}
              </div>

              {/* Exec approval */}
              <div style={{ marginTop: 10, padding: 10, background: form.execApproved ? `${C.success}10` : C.white, borderRadius: 6, border: `1px solid ${form.execApproved ? C.success : C.border}` }}>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.execApproved}
                    onChange={e => setForm({ ...form, execApproved: e.target.checked, execApprovalDate: e.target.checked ? new Date().toISOString().slice(0, 10) : "" })}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>موافقة المدير التنفيذي</span>
                  {form.execApproved && (
                    <input
                      type="date"
                      value={form.execApprovalDate}
                      onChange={e => setForm({ ...form, execApprovalDate: e.target.value })}
                      style={{ marginRight: "auto", padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12 }}
                    />
                  )}
                </label>
              </div>
            </div>

            <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Gavel} onClick={handleSave}>حفظ القرار</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_DECISION); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Committees Tab — اللجان
   ───────────────────────────────────────────── */
const INITIAL_COMMITTEE = {
  name: "", description: "", chair: "", members: [],
  goals: "", authorities: "", foundingDate: "",
  meetingFrequency: "شهري", statusLabel: "نشطة",
};
const COMMITTEE_STATUSES_OPTIONS = ["نشطة", "معلّقة", "منتهية"];
const STATUS_LABEL_TO_KEY = { "نشطة": "active", "معلّقة": "paused", "منتهية": "ended" };

function CommitteesTab({ canEdit, committees: committeesProp, setCommittees: setCommitteesProp, members = [] }) {
  const [committeesLocal, setCommitteesLocal] = useState([]);
  const committees = committeesProp !== undefined ? committeesProp : committeesLocal;
  const setCommittees = setCommitteesProp || setCommitteesLocal;
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_COMMITTEE);
  const [showToast, ToastView] = useToast();

  function toggleMember(name) {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(name)
        ? prev.members.filter(n => n !== name)
        : [...prev.members, name],
    }));
  }

  async function handleSave() {
    if (!form.name || !form.chair) {
      showToast("الاسم والرئيس مطلوبان", "error");
      return;
    }
    const allMembers = form.members.includes(form.chair) ? form.members : [form.chair, ...form.members];
    const payload = {
      name: form.name,
      description: form.description,
      chair: form.chair,
      status: STATUS_LABEL_TO_KEY[form.statusLabel] || "active",
      color: "#7c3aed",
      members: allMembers,
      goals: form.goals,
      authorities: form.authorities,
      founding_date: form.foundingDate || new Date().toISOString().slice(0, 10),
      meeting_frequency: form.meetingFrequency,
      decisions_count: 0,
      meetings_count: 0,
    };
    try {
      const created = await createBoardCommittee(payload);
      setCommittees(prev => [...prev, mapCommitteeRow(created)]);
      setForm(INITIAL_COMMITTEE);
      setShowAdd(false);
      showToast("تم إنشاء اللجنة");
    } catch (e) {
      showToast(e.message || "تعذّر إنشاء اللجنة", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      {/* KPIs */}
      <div style={styles.kpiRow}>
        <KPICard title="عدد اللجان" value={committees.length} icon={Layers} color="#6366f1" />
        <KPICard title="أعضاء اللجان" value={committees.reduce((s, c) => s + c.members.length, 0)} icon={Users} color={C.teal} />
        <KPICard title="لجان نشطة" value={committees.filter(c => c.status === "active").length} icon={CheckCircle} color={C.success} />
      </div>

      {/* Toolbar */}
      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إنشاء لجنة</Btn>
        </div>
      )}

      {/* Committees Grid */}
      <div style={styles.committeesGrid}>
        {committees.map(c => (
          <Card key={c.id} style={styles.committeeCard} onClick={() => setSelected(c)}>
            <div style={styles.committeeHeader}>
              <div style={{ ...styles.committeeIcon, background: `${c.color}15` }}>
                <Layers size={22} color={c.color} />
              </div>
              <div style={{
                padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: c.status === "active" ? `${C.success}15` : `${C.textLight}15`,
                color: c.status === "active" ? C.success : C.textLight,
              }}>
                {c.status === "active" ? "نشطة" : "معلّقة"}
              </div>
            </div>
            <h4 style={{ margin: "12px 0 4px", fontSize: 16, fontWeight: 700, color: C.primary }}>{c.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{c.description}</p>
            <div style={styles.committeeFooter}>
              <span style={styles.committeeStat}>
                <Users size={14} /> {c.members.length} أعضاء
              </span>
              <span style={styles.committeeStat}>
                <Gavel size={14} /> {c.decisionsCount} قرار
              </span>
              <span style={styles.committeeStat}>
                <Calendar size={14} /> {c.meetingsCount} اجتماع
              </span>
            </div>
            <div style={styles.memberAvatars}>
              {c.members.slice(0, 4).map((m, i) => (
                <div key={i} style={{ ...styles.miniAvatar, zIndex: 4 - i, marginRight: i > 0 ? -8 : 0 }}>
                  {m.charAt(0)}
                </div>
              ))}
              {c.members.length > 4 && (
                <span style={{ fontSize: 12, color: C.textLight, marginRight: 4 }}>+{c.members.length - 4}</span>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Committee Detail */}
      {selected && (
        <Modal title={`لجنة — ${selected.name}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>الرئيس:</strong> {selected.chair}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> {selected.status === "active" ? "نشطة" : selected.status === "paused" ? "معلّقة" : "منتهية"}</div>
            {selected.foundingDate && <div style={styles.detailRow}><strong>تاريخ التأسيس:</strong> {selected.foundingDate}</div>}
            {selected.meetingFrequency && <div style={styles.detailRow}><strong>دورية الاجتماعات:</strong> {selected.meetingFrequency}</div>}
            {selected.goals && (
              <div style={styles.detailRow}>
                <strong>الأهداف:</strong>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary, lineHeight: 1.7 }}>{selected.goals}</p>
              </div>
            )}
            {selected.authorities && (
              <div style={styles.detailRow}>
                <strong>الصلاحيات:</strong>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary, lineHeight: 1.7 }}>{selected.authorities}</p>
              </div>
            )}
            <div style={styles.detailRow}>
              <strong>الأعضاء ({selected.members.length}):</strong>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                {selected.members.map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                    <div style={styles.miniAvatar}>{m.charAt(0)}</div>
                    <span>{m}</span>
                    {m === selected.chair && <span style={{ fontSize: 10, color: C.accent, fontWeight: 700 }}>(رئيس)</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Committee */}
      {showAdd && (
        <Modal title="إنشاء لجنة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_COMMITTEE); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم اللجنة" required placeholder="مثال: لجنة الاستثمار" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="وصف اللجنة ودورها" type="textarea" placeholder="وصف عام عن اللجنة..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <Field
              label="رئيس اللجنة"
              type="select"
              options={members.map(m => m.name)}
              required
              value={form.chair}
              onChange={v => setForm({ ...form, chair: v })}
            />

            {/* Members multi-select */}
            <div>
              <label style={{ display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600, color: C.textLight }}>
                أعضاء اللجنة ({form.members.length} مختار)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: 12, background: C.bg, borderRadius: 8, maxHeight: 180, overflowY: "auto" }}>
                {members.map(m => (
                  <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.members.includes(m.name)}
                      onChange={() => toggleMember(m.name)}
                    />
                    <span>{m.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="أهداف اللجنة" type="textarea" placeholder="الأهداف الاستراتيجية للجنة..." rows={3} value={form.goals} onChange={v => setForm({ ...form, goals: v })} />
            <Field label="صلاحيات اللجنة" type="textarea" placeholder="ما يحق للجنة اتخاذه من قرارات..." rows={3} value={form.authorities} onChange={v => setForm({ ...form, authorities: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="تاريخ التأسيس" type="date" value={form.foundingDate} onChange={v => setForm({ ...form, foundingDate: v })} />
              <Field label="دورية الاجتماعات" type="select" options={["شهري", "ربع سنوي", "حسب الحاجة"]} value={form.meetingFrequency} onChange={v => setForm({ ...form, meetingFrequency: v })} />
              <Field label="الحالة" type="select" options={COMMITTEE_STATUSES_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Layers} onClick={handleSave}>إنشاء اللجنة</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_COMMITTEE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Board Meetings Tab — اجتماعات مفصّلة
   ───────────────────────────────────────────── */
const INITIAL_MEETING = {
  title: "", date: "", time: "", type: "دوري",
  link: "", attendees: "", absentees: "", agenda: "",
  minutes: "", minutesFile: "",
};

const MEETING_TYPES = ["دوري", "غير دوري"];
const MEETING_TYPE_COLORS = { "دوري": "#7c3aed", "غير دوري": C.teal };

export function BoardMeetingsTab({ canEdit, meetings: meetingsProp, setMeetings: setMeetingsProp }) {
  const meetings = meetingsProp || [];
  const setMeetings = setMeetingsProp || (() => {});
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_MEETING);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.title || !form.date) {
      showToast("العنوان والتاريخ مطلوبان", "error");
      return;
    }
    try {
      // الأعمدة المخزّنة فقط: module/title/meeting_date/location/attendees/notes.
      // الحقول الغنية (type/time/link/absentees/agenda/minutes/minutesFile) عرض-فقط ولا تصمد بعد إعادة التحميل.
      const saved = await createMeeting({
        module: "board",
        title: form.title,
        meeting_date: form.date,
        location: "",
        attendees: splitBoardList(form.attendees).join("، "),
        notes: "",
      });
      if (saved) setMeetings(prev => [buildBoardMeeting(saved, form), ...prev]);
      setForm(INITIAL_MEETING);
      setShowAdd(false);
      showToast("تم حفظ الاجتماع بنجاح");
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ الاجتماع", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      {/* KPIs */}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الاجتماعات" value={meetings.length} icon={Calendar} color="#7c3aed" />
        <KPICard title="مجدول" value={meetings.filter(m => m.status === "scheduled").length} icon={Clock} color={C.warning} />
        <KPICard title="منعقد" value={meetings.filter(m => m.status === "completed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="مع محضر" value={meetings.filter(m => m.minutes || m.minutesFile).length} icon={FileSignature} color={C.teal} />
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.primary, margin: 0 }}>اجتماعات مجلس الإدارة</h3>
        {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">جدولة اجتماع جديد</Btn>}
      </div>

      {/* Meetings cards */}
      <div style={{ display: "grid", gap: 20 }}>
        {meetings.map(m => (
          <Card key={m.id} style={{ borderRight: `4px solid ${MEETING_TYPE_COLORS[m.type] || C.teal}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.primary }}>{m.title}</h4>
                  <span style={{
                    padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                    background: `${MEETING_TYPE_COLORS[m.type] || C.teal}15`,
                    color: MEETING_TYPE_COLORS[m.type] || C.teal,
                  }}>{m.type}</span>
                </div>
                <div style={{ display: "flex", gap: 20, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={13} /> {m.date}</span>
                  {m.time && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={13} /> {m.time}</span>}
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={13} /> {m.attendees.length} حضور</span>
                  {m.absentees.length > 0 && <span style={{ display: "flex", alignItems: "center", gap: 4 }}>غياب: {m.absentees.length}</span>}
                </div>
              </div>
              <span style={{
                padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                background: m.status === "completed" ? `${C.success}15` : `${C.warning}15`,
                color: m.status === "completed" ? C.success : C.warning,
              }}>
                {m.status === "completed" ? "منعقد" : "مجدول"}
              </span>
            </div>

            {m.link && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: `${C.teal}08`, borderRadius: 10, marginBottom: 10 }}>
                <Video size={15} color={C.teal} />
                <span style={{ fontSize: 13, color: C.teal, fontWeight: 600 }}>رابط الاجتماع:</span>
                <a href={m.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.primary, wordBreak: "break-all" }}>{m.link}</a>
              </div>
            )}

            {m.attendees.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.success, marginLeft: 6 }}>الحضور:</span>
                <span style={{ fontSize: 13, color: C.text }}>{m.attendees.join("، ")}</span>
              </div>
            )}

            {m.absentees.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.danger, marginLeft: 6 }}>الغياب:</span>
                <span style={{ fontSize: 13, color: C.text }}>{m.absentees.join("، ")}</span>
              </div>
            )}

            {m.agenda && (
              <div style={{ padding: 10, background: C.bg, borderRadius: 8, marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.primary }}>جدول الأعمال:</p>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{m.agenda}</p>
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              {(m.minutes || m.minutesFile) && (
                <Btn variant="ghost" size="sm" icon={FileSignature} onClick={() => setSelected(m)}>
                  عرض المحضر
                </Btn>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Detail Modal: minutes */}
      {selected && (
        <Modal title={`محضر اجتماع — ${selected.title}`} onClose={() => setSelected(null)} width={650}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 16, fontSize: 13, color: C.textLight, flexWrap: "wrap" }}>
              <span><Calendar size={13} /> {selected.date}</span>
              {selected.time && <span><Clock size={13} /> {selected.time}</span>}
              <span style={{ padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: `${MEETING_TYPE_COLORS[selected.type]}15`, color: MEETING_TYPE_COLORS[selected.type] }}>{selected.type}</span>
            </div>

            {selected.attendees.length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.success }}>الحضور ({selected.attendees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.attendees.join("، ")}</p>
              </div>
            )}

            {selected.absentees.length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.danger }}>الغياب ({selected.absentees.length}):</p>
                <p style={{ margin: "4px 0 0", fontSize: 14, color: C.primary }}>{selected.absentees.join("، ")}</p>
              </div>
            )}

            {selected.agenda && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>جدول الأعمال:</p>
                <p style={{ margin: "4px 0 0", padding: 12, background: C.bg, borderRadius: 8, fontSize: 14, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{selected.agenda}</p>
              </div>
            )}

            {selected.minutes && (
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>محضر الاجتماع:</p>
                <div style={{ margin: "4px 0 0", padding: 14, background: `${C.teal}06`, border: `1px solid ${C.teal}30`, borderRadius: 10, fontSize: 14, color: C.text, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{selected.minutes}</div>
              </div>
            )}

            {selected.minutesFile && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: `${C.accent}10`, borderRadius: 10 }}>
                <Paperclip size={16} color={C.accent} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>مرفق المحضر:</span>
                <span style={{ fontSize: 13, color: C.text }}>{selected.minutesFile}</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Add Modal */}
      {showAdd && (
        <Modal title="جدولة اجتماع جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_MEETING); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="عنوان الاجتماع" required placeholder="مثال: الاجتماع الدوري 27" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="الوقت" type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
              <Field label="نوع الاجتماع" type="select" options={MEETING_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
            </div>
            <Field label="رابط الاجتماع الإلكتروني" placeholder="https://zoom.us/..." value={form.link} onChange={v => setForm({ ...form, link: v })} />

            <Field label="الحضور (مفصولة بفواصل)" type="textarea" rows={2} placeholder="عبدالله الصالح، خالد العتيبي، سعد الدوسري..." value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} />
            <Field label="الغياب (مفصولة بفواصل)" type="textarea" rows={2} placeholder="أسماء الغائبين..." value={form.absentees} onChange={v => setForm({ ...form, absentees: v })} />

            <Field label="جدول الأعمال" type="textarea" rows={5} placeholder="1. مراجعة محضر الاجتماع السابق&#10;2. مناقشة التقرير المالي&#10;3. ..." value={form.agenda} onChange={v => setForm({ ...form, agenda: v })} />

            <Field label="محضر الاجتماع" type="textarea" rows={6} placeholder="نص محضر الاجتماع التفصيلي..." value={form.minutes} onChange={v => setForm({ ...form, minutes: v })} />

            <div>
              <Field label="مرفق المحضر (اسم الملف)" placeholder="محضر_27.pdf" value={form.minutesFile} onChange={v => setForm({ ...form, minutesFile: v })} />
              <p style={{ margin: "-8px 0 0", fontSize: 11, color: C.textLight, fontStyle: "italic" }}>
                سيتم رفع الملف لاحقاً عند ربط التخزين
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Calendar} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_MEETING); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== مساعدات اجتماعات المجلس (CRUD حقيقي، module="board") =====
const splitBoardList = s => s ? s.split(/،|,|\n/).map(x => x.trim()).filter(Boolean) : [];

// صف Supabase → شكل واجهة المجلس. الأعمدة المخزّنة فقط: title/meeting_date/attendees.
// بقية الحقول (type/time/link/absentees/agenda/minutes/minutesFile) عرض-فقط وتُفرَّغ بعد إعادة التحميل.
const mapBoardRow = (row) => ({
  id: row.id,
  title: row.title || "",
  date: row.meeting_date || "",
  time: "",
  type: "",
  link: "",
  attendees: splitBoardList(row.attendees),
  absentees: [],
  agenda: "",
  minutes: "",
  minutesFile: "",
  status: row.meeting_date && row.meeting_date < new Date().toISOString().slice(0, 10)
    ? "completed" : "scheduled",
});

// يدمج الحقول الغنية من النموذج فوق الصف المحفوظ لعرضها في الجلسة الحالية فقط.
const buildBoardMeeting = (savedRow, form) => ({
  ...mapBoardRow(savedRow),
  time: form.time || "",
  type: form.type,
  link: form.link || "",
  attendees: splitBoardList(form.attendees),
  absentees: splitBoardList(form.absentees),
  agenda: form.agenda || "",
  minutes: form.minutes || "",
  minutesFile: form.minutesFile || "",
  status: form.minutes ? "completed" : "scheduled",
});

/* ═════════════════════════════════════════════
   ✦ Quick Decision Form Modal
   ═════════════════════════════════════════════ */
function QuickDecisionForm({ onClose, onSave, decisionsCount, meetings = [], members = [] }) {
  const autoNumber = () => {
    const seq = decisionsCount + 1;
    const yr = new Date().getFullYear();
    return `ق-${yr}-${String(seq).padStart(3, "0")}`;
  };
  const [form, setForm] = useState({ ...INITIAL_DECISION, number: autoNumber() });
  const labelToKey = { "معتمد": "approved", "قيد الدراسة": "pending", "تم التنفيذ": "implemented", "مرفوض": "rejected", "متأخر": "overdue" };
  const signedCount = Object.keys(form.signatures || {}).length;

  function toggleSignature(name) {
    setForm(prev => {
      const newSigs = { ...prev.signatures };
      const newDates = { ...prev.signatureDates };
      if (newSigs[name]) {
        delete newSigs[name];
        delete newDates[name];
      } else {
        newSigs[name] = true;
        newDates[name] = new Date().toISOString().slice(0, 10);
      }
      return { ...prev, signatures: newSigs, signatureDates: newDates };
    });
  }

  async function handleSave() {
    if (!form.title || !form.details || !form.responsible) {
      alert("يرجى تعبئة الحقول المطلوبة: عنوان القرار، التفاصيل، المسؤول");
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      number: form.number || autoNumber(),
      meeting: form.meeting || "—",
      decision_date: today,
      title: form.title,
      details: form.details,
      responsible: form.responsible,
      deadline: form.deadline || "",
      status: labelToKey[form.statusLabel] || "pending",
      notes: form.notes || null,
      signatures: { ...form.signatures },
      signature_dates: { ...form.signatureDates },
      exec_approved: form.execApproved,
      exec_approval_date: form.execApprovalDate || (form.execApproved ? today : ""),
    };
    try {
      const created = await createBoardDecision(payload);
      onSave(mapDecisionRow(created));
      onClose();
    } catch (e) {
      alert(e.message || "تعذّر حفظ القرار");
    }
  }

  return (
    <Modal title="إضافة قرار جديد" onClose={onClose} width={720}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="رقم القرار (يولّد تلقائياً)" placeholder="ق-2026-001" value={form.number} onChange={v => setForm({ ...form, number: v })} />
          <Field label="الاجتماع المرتبط" type="select" options={meetings.map(m => m.title)} value={form.meeting} onChange={v => setForm({ ...form, meeting: v })} />
        </div>
        <Field label="عنوان القرار" required placeholder="أدخل عنوان القرار" value={form.title} onChange={v => setForm({ ...form, title: v })} />
        <Field label="تفاصيل القرار" type="textarea" required placeholder="نص القرار بالتفصيل..." rows={5} value={form.details} onChange={v => setForm({ ...form, details: v })} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="المسؤول عن التنفيذ" required placeholder="اسم المسؤول" value={form.responsible} onChange={v => setForm({ ...form, responsible: v })} />
          <Field label="الموعد النهائي" type="date" value={form.deadline} onChange={v => setForm({ ...form, deadline: v })} />
          <Field label="الحالة" type="select" options={["قيد الدراسة","معتمد","تم التنفيذ","مرفوض","متأخر"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
        </div>
        <div style={{ padding: 12, background: `${C.teal}05`, borderRadius: 10, border: `1px solid ${C.teal}20` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong style={{ color: C.primary, display: "flex", alignItems: "center", gap: 6 }}>
              <FileSignature size={16} color={C.teal} /> التوقيع الإلكتروني لأعضاء المجلس
            </strong>
            <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: `${C.teal}15`, color: C.teal }}>
              الموافقات: {signedCount}/{members.length} أعضاء {form.execApproved ? "+ المدير التنفيذي (معتمد)" : "+ المدير التنفيذي (بانتظار)"}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxHeight: 220, overflowY: "auto" }}>
            {members.map(m => {
              const ok = !!form.signatures[m.name];
              return (
                <label key={m.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 10px", background: C.white, borderRadius: 6, fontSize: 12,
                  border: `1px solid ${ok ? C.success : C.border}`, cursor: "pointer",
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input type="checkbox" checked={ok} onChange={() => toggleSignature(m.name)} />
                    <span>{m.name}</span>
                  </span>
                  {ok && <span style={{ fontSize: 10, color: C.textLight }}>{form.signatureDates[m.name]}</span>}
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 10, padding: 10, background: form.execApproved ? `${C.success}10` : C.white, borderRadius: 6, border: `1px solid ${form.execApproved ? C.success : C.border}` }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.execApproved}
                onChange={e => setForm({ ...form, execApproved: e.target.checked, execApprovalDate: e.target.checked ? new Date().toISOString().slice(0, 10) : "" })}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>موافقة المدير التنفيذي</span>
              {form.execApproved && (
                <input
                  type="date"
                  value={form.execApprovalDate}
                  onChange={e => setForm({ ...form, execApprovalDate: e.target.value })}
                  style={{ marginRight: "auto", padding: "4px 8px", borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12 }}
                />
              )}
            </label>
          </div>
        </div>
        <Field label="ملاحظات" type="textarea" placeholder="ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn icon={Gavel} onClick={handleSave}>حفظ القرار</Btn>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════
   ✦ Quick Committee Form Modal
   ═════════════════════════════════════════════ */
function QuickCommitteeForm({ onClose, onSave, members = [] }) {
  const [form, setForm] = useState(INITIAL_COMMITTEE);

  function toggleMember(name) {
    setForm(prev => ({
      ...prev,
      members: prev.members.includes(name) ? prev.members.filter(n => n !== name) : [...prev.members, name],
    }));
  }

  async function handleSave() {
    if (!form.name || !form.chair) {
      alert("الاسم والرئيس مطلوبان");
      return;
    }
    const allMembers = form.members.includes(form.chair) ? form.members : [form.chair, ...form.members];
    const payload = {
      name: form.name,
      description: form.description,
      chair: form.chair,
      status: STATUS_LABEL_TO_KEY[form.statusLabel] || "active",
      color: "#7c3aed",
      members: allMembers,
      goals: form.goals,
      authorities: form.authorities,
      founding_date: form.foundingDate || new Date().toISOString().slice(0, 10),
      meeting_frequency: form.meetingFrequency,
      decisions_count: 0,
      meetings_count: 0,
    };
    try {
      const created = await createBoardCommittee(payload);
      onSave(mapCommitteeRow(created));
      onClose();
    } catch (e) {
      alert(e.message || "تعذّر إنشاء اللجنة");
    }
  }

  return (
    <Modal title="إنشاء لجنة جديدة" onClose={onClose} width={680}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="اسم اللجنة" required placeholder="مثال: لجنة الاستثمار" value={form.name} onChange={v => setForm({ ...form, name: v })} />
        <Field label="وصف اللجنة ودورها" type="textarea" placeholder="وصف عام عن اللجنة..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
        <Field label="رئيس اللجنة" type="select" options={members.map(m => m.name)} required value={form.chair} onChange={v => setForm({ ...form, chair: v })} />
        <div>
          <label style={{ display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600, color: C.textLight }}>
            أعضاء اللجنة ({form.members.length} مختار)
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, padding: 12, background: C.bg, borderRadius: 8, maxHeight: 180, overflowY: "auto" }}>
            {members.map(m => (
              <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={form.members.includes(m.name)} onChange={() => toggleMember(m.name)} />
                <span>{m.name}</span>
              </label>
            ))}
          </div>
        </div>
        <Field label="أهداف اللجنة" type="textarea" placeholder="الأهداف الاستراتيجية للجنة..." rows={3} value={form.goals} onChange={v => setForm({ ...form, goals: v })} />
        <Field label="صلاحيات اللجنة" type="textarea" placeholder="ما يحق للجنة اتخاذه من قرارات..." rows={3} value={form.authorities} onChange={v => setForm({ ...form, authorities: v })} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="تاريخ التأسيس" type="date" value={form.foundingDate} onChange={v => setForm({ ...form, foundingDate: v })} />
          <Field label="دورية الاجتماعات" type="select" options={["شهري", "ربع سنوي", "حسب الحاجة"]} value={form.meetingFrequency} onChange={v => setForm({ ...form, meetingFrequency: v })} />
          <Field label="الحالة" type="select" options={COMMITTEE_STATUSES_OPTIONS} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn icon={Layers} onClick={handleSave}>إنشاء اللجنة</Btn>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════
   ✦ Quick Meeting Form Modal
   ═════════════════════════════════════════════ */
function QuickMeetingForm({ onClose, onSave }) {
  const [form, setForm] = useState(INITIAL_MEETING);

  async function handleSave() {
    if (!form.title || !form.date) {
      alert("العنوان والتاريخ مطلوبان");
      return;
    }
    try {
      const saved = await createMeeting({
        module: "board",
        title: form.title,
        meeting_date: form.date,
        location: "",
        attendees: splitBoardList(form.attendees).join("، "),
        notes: "",
      });
      onSave(saved ? buildBoardMeeting(saved, form) : null);
      onClose();
    } catch (e) {
      alert(e?.message || "تعذّر حفظ الاجتماع");
    }
  }

  return (
    <Modal title="جدولة اجتماع جديد" onClose={onClose} width={720}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="عنوان الاجتماع" required placeholder="مثال: الاجتماع الدوري 27" value={form.title} onChange={v => setForm({ ...form, title: v })} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
          <Field label="الوقت" type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
          <Field label="نوع الاجتماع" type="select" options={MEETING_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
        </div>
        <Field label="رابط الاجتماع الإلكتروني" placeholder="https://zoom.us/..." value={form.link} onChange={v => setForm({ ...form, link: v })} />
        <Field label="الحضور (مفصولة بفواصل)" type="textarea" rows={2} placeholder="عبدالله الصالح، خالد العتيبي، سعد الدوسري..." value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} />
        <Field label="الغياب (مفصولة بفواصل)" type="textarea" rows={2} placeholder="أسماء الغائبين..." value={form.absentees} onChange={v => setForm({ ...form, absentees: v })} />
        <Field label="جدول الأعمال" type="textarea" rows={5} placeholder="1. مراجعة محضر الاجتماع السابق&#10;2. مناقشة التقرير المالي&#10;3. ..." value={form.agenda} onChange={v => setForm({ ...form, agenda: v })} />
        <Field label="محضر الاجتماع" type="textarea" rows={6} placeholder="نص محضر الاجتماع التفصيلي..." value={form.minutes} onChange={v => setForm({ ...form, minutes: v })} />
        <div>
          <Field label="مرفق المحضر (اسم الملف)" placeholder="محضر_27.pdf" value={form.minutesFile} onChange={v => setForm({ ...form, minutesFile: v })} />
          <p style={{ margin: "-8px 0 0", fontSize: 11, color: C.textLight, fontStyle: "italic" }}>
            سيتم رفع الملف لاحقاً عند ربط التخزين
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn icon={Calendar} onClick={handleSave}>حفظ</Btn>
          <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        </div>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 20,
    marginBottom: 16,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: C.bg,
    borderRadius: 8,
    padding: "6px 12px",
    border: `1px solid ${C.border}`,
    flex: 1,
    maxWidth: 320,
  },
  searchInput: {
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: 13,
    fontFamily: "'Readex Pro', sans-serif",
    color: C.primary,
    width: "100%",
  },
  filterSelect: {
    padding: "7px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    fontFamily: "'Readex Pro', sans-serif",
    color: C.primary,
    background: C.white,
    cursor: "pointer",
  },

  /* Members Grid */
  membersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 16,
  },
  memberCard: {
    padding: 20,
    cursor: "pointer",
    transition: "all 0.2s",
    position: "relative",
  },
  memberTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  memberAvatar: {
    width: 48,
    height: 48,
    borderRadius: 12,
    color: C.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 20,
  },
  memberStatusDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: `2px solid ${C.white}`,
  },
  memberName: {
    margin: "12px 0 2px",
    fontSize: 15,
    fontWeight: 700,
    color: C.primary,
  },
  memberPosition: {
    margin: 0,
    fontSize: 13,
    color: C.teal,
    fontWeight: 600,
  },
  memberMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginTop: 10,
  },
  memberMetaItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: C.textLight,
  },
  memberTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 10,
  },
  tag: {
    padding: "3px 8px",
    background: `${C.teal}12`,
    color: C.teal,
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
  },

  /* Committees Grid */
  committeesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
  },
  committeeCard: {
    padding: 20,
    cursor: "pointer",
  },
  committeeHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  committeeIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  committeeFooter: {
    display: "flex",
    gap: 20,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  },
  committeeStat: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: C.textLight,
  },
  memberAvatars: {
    display: "flex",
    alignItems: "center",
    marginTop: 10,
  },
  miniAvatar: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 11,
    border: `2px solid ${C.white}`,
  },

  /* Detail */
  detailRow: {
    fontSize: 14,
    color: C.primary,
    lineHeight: 1.6,
  },
};
